import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { EditorView } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';

// Feature K — tap-to-comment. A read-only-only ProseMirror plugin that puts a
// per-block "+" comment target at the corner of every audio/visual script cell.
// A "block comment" is just a ranged comment whose range spans the whole cell,
// so this reuses the existing comment model end-to-end — it only adds the way
// to CREATE one on touch or by keyboard (the button is a real, focusable
// <button>, so Tab + Enter/Space work for free). Desktop text-selection is
// untouched. Add this extension only to the client review's read-only editor.

export const BLOCK_COMMENT_EVENT = 'hsp-block-comment';

export interface BlockCommentDetail {
  from: number;
  to: number;
  text: string;
}

const key = new PluginKey<DecorationSet>('hsp-block-comment-targets');

function buildTargets(doc: PMNode): DecorationSet {
  const decos: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name !== 'scriptCell') return true;
    const from = pos + 1;
    const to = pos + node.nodeSize - 1;
    const text = doc.textBetween(from, to, ' ', ' ').trim();
    // Don't offer a target on an empty cell — nothing to comment on.
    if (text.length === 0) return false;
    decos.push(
      Decoration.widget(from, (view) => makeButton(view, from, to), {
        side: -1,
        key: `bc-${pos}`,
        ignoreSelection: true,
      }),
    );
    return false; // cells don't nest — don't descend into the paragraphs.
  });
  return DecorationSet.create(doc, decos);
}

function makeButton(view: EditorView, from: number, to: number): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'block-comment-target';
  btn.setAttribute('contenteditable', 'false');
  btn.setAttribute('aria-label', 'Comment on this block');
  btn.title = 'Comment on this block';
  btn.tabIndex = 0;
  btn.textContent = '+';

  const fire = () => {
    const text = view.state.doc.textBetween(from, to, ' ', ' ').trim();
    view.dom.dispatchEvent(
      new CustomEvent<BlockCommentDetail>(BLOCK_COMMENT_EVENT, {
        bubbles: true,
        detail: { from, to, text },
      }),
    );
  };

  // preventDefault on mousedown so the click doesn't try to move the (absent,
  // read-only) selection into the widget.
  btn.addEventListener('mousedown', (e) => e.preventDefault());
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    fire();
  });
  btn.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fire();
    }
  });
  return btn;
}

export const BlockCommentTargets = Extension.create({
  name: 'blockCommentTargets',
  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key,
        state: {
          init: (_config, { doc }) => buildTargets(doc),
          apply: (tr, old) => (tr.docChanged ? buildTargets(tr.doc) : old),
        },
        props: {
          decorations(state) {
            return key.getState(state) ?? DecorationSet.empty;
          },
        },
      }),
    ];
  },
});
